import { useState } from "react";
import Section from "../Section";
import { useTranslations } from "../../contexts/Translation";
import { H1, H3 } from "../Headers/Headings";
import Button, { LinkButton } from "../Button/Button";
import { useNavigate } from "react-router-dom";
import { useApiData } from "../../contexts/ApiData";
import { useOrganizations, useRole } from "../../contexts/UserProvider";
import { CheckCircle } from "@emotion-icons/material/CheckCircle";
import styles from "./PricingPlans.module.css";

type PlanFeature = {
  key: string;
};

type BillingOption = {
  lookupKey: string;
  label: string;
  price: string;
  priceNote: string;
  badge?: string;
};

type PlanProps = {
  testId: string;
  title: string;
  price: string;
  priceNote: string;
  features: PlanFeature[];
  ctaLabel: string;
  onCta: (lookupKey?: string) => void;
  highlighted?: boolean;
  billingOptions?: BillingOption[];
  disabled?: boolean;
  note?: string;
};

const Plan = ({ testId, title, price, priceNote, features, ctaLabel, onCta, highlighted, billingOptions, disabled, note }: PlanProps) => {
  const [selectedBilling, setSelectedBilling] = useState(billingOptions?.[0]?.lookupKey);
  const activeBilling = billingOptions?.find((o) => o.lookupKey === selectedBilling) ?? billingOptions?.[0];

  const displayPrice = activeBilling?.price ?? price;
  const displayPriceNote = activeBilling?.priceNote ?? priceNote;

    return (
    <div className={[styles.plan, highlighted && styles.planHighlighted].filter(Boolean).join(" ")} data-testid={`pricing-plan-${testId}`}>
      <H3 variant='compact' color={highlighted ? "primary" : "text"}>
        {title}
      </H3>

      {billingOptions && (
        <div className={styles.billingToggle}>
          {billingOptions.map((opt) => (
            <button
              key={opt.lookupKey}
              className={[styles.billingOption, selectedBilling === opt.lookupKey && styles.billingOptionActive].filter(Boolean).join(" ")}
              data-testid={`pricing-plan-${testId}-billing-${opt.lookupKey}`}
              onClick={() => setSelectedBilling(opt.lookupKey)}
              type='button'
            >
              {opt.label}
              {opt.badge && <span className={styles.badge} data-testid={`pricing-plan-${testId}-badge`}>{opt.badge}</span>}
            </button>
          ))}
        </div>
      )}

      <div>
        <div className={styles.priceValue} data-testid={`pricing-plan-${testId}-price`}>{displayPrice}</div>
        <div className={styles.priceNote} data-testid={`pricing-plan-${testId}-price-note`}>{displayPriceNote}</div>
      </div>
      <div className={styles.featureList}>
        {features.map((f) => (
          <div key={f.key} className={styles.featureItem}>
            <CheckCircle size='1.1rem' />
            <div className={styles.featureText}>{f.key}</div>
          </div>
        ))}
      </div>
      <Button
        className={styles.planButton}
        disabled={disabled}
        onClick={() => onCta(activeBilling?.lookupKey)}
        testId={`pricing-plan-${testId}-cta`}
        variant={highlighted ? "primary" : "secondary"}
      >
        {ctaLabel}
      </Button>
      {note && (
        <p className={styles.planNote} data-testid={`pricing-plan-${testId}-owner-only`}>
          {note}
        </p>
      )}
    </div>
  );
};

type PricingPlansProps = {
  afterOrgRegistration?: boolean;
};

const PricingPlans = ({ afterOrgRegistration = false }: PricingPlansProps) => {
  const t = useTranslations("pricing");
  const navigate = useNavigate();
  const { post } = useApiData();
  const { organizations } = useOrganizations();
  const role = useRole();

  const hasOrganization = afterOrgRegistration || (organizations !== null && organizations.length > 0);
  // Only the owner can buy the org plan. Right after registration the user is the owner, even if role is not loaded yet
  const orgPlanForOwnerOnly = hasOrganization && !afterOrgRegistration && role !== "owner";

  const goToCheckout = async (lookupKey: string) => {
    const response = await post("/payment/create-checkout-session", { lookupKey });
    if (response?.gotoUrl) {
      window.location.href = response.gotoUrl;
    }
  };

  const singleBillingOptions: BillingOption[] = [
    {
      lookupKey: "fj-single-yearly",
      label: t("singleBillingYearly"),
      price: t("singlePriceYearly"),
      priceNote: t("singlePriceNoteYearly"),
      badge: t("singleBadgeYearly"),
    },
    {
      lookupKey: "fj-single-3m",
      label: t("singleBillingQuarterly"),
      price: t("singlePriceQuarterly"),
      priceNote: t("singlePriceNoteQuarterly"),
    },
  ];

  return (
    <Section>
      <H1 align='center'>{t("heading")}</H1>
      <p className={styles.subheading}>{t("subheading")}</p>

      <div className={styles.plans}>
        <Plan
          testId='trial'
          title={t("trialTitle")}
          price={t("trialPrice")}
          priceNote={t("trialPriceNote")}
          features={[{ key: t("trialFeature1") }, { key: t("trialFeature2") }, { key: t("trialFeature3") }]}
          ctaLabel={t("trialCta")}
          onCta={() => navigate("/")}
        />
        <Plan
          testId='single'
          title={t("singleTitle")}
          price={t("singlePriceYearly")}
          priceNote={t("singlePriceNoteYearly")}
          billingOptions={singleBillingOptions}
          features={[{ key: t("singleFeature1") }, { key: t("singleFeature2") }, { key: t("singleFeature3") }]}
          ctaLabel={t("singleCta")}
          onCta={(lookupKey) => goToCheckout(lookupKey ?? "fj-single-yearly")}
          highlighted
        />
        <Plan
          testId='organization'
          title={t("orgTitle")}
          price={t("orgPrice")}
          priceNote={t("orgPriceNote")}
          features={[{ key: t("orgFeature1") }, { key: t("orgFeature2") }, { key: t("orgFeature3") }, { key: t("orgFeature4") }]}
          ctaLabel={t("orgCta")}
          onCta={() => hasOrganization ? goToCheckout("fj-org-yearly") : navigate("/register-organization")}
          disabled={orgPlanForOwnerOnly}
          note={orgPlanForOwnerOnly ? t("orgOwnerOnly") : undefined}
        />
      </div>

      <div className={styles.footerAction}>
        <LinkButton onClick={() => navigate("/join-organization")} testId='pricing-join-organization'>
          {t("joinOrganization")}
        </LinkButton>
      </div>

      <p className={styles.vatNote}>{t("vatNote")}</p>
    </Section>
  );
};

export default PricingPlans;
